/**
 * Evalue les regles de design a partir des faits de l'eleve.
 * Les calculs restent purs pour que le lecteur et l'aperçu enseignant
 * produisent le meme verdict sur une creation.
 */
import {
  TGDAxis, TGDComponent, TGDComparableProp, TGDDesign, TGDHarmony,
  TGDRule, TGDSelector, TGDZone,
} from '../../../models/courses/graphicDesign';
import { contrastRatio, deltaE76, distinctColors, hexToHsl, isNeutral, normalizeHex } from './color';
import { salienceOf } from './salience';
import { selectComponents, sizeOf } from './selectors';

export interface TGDFacts {
  design: TGDDesign;
  initial?: TGDDesign;
  answers: TGDAnswers;
  elapsedMs: number;
  artboard: { width: number; height: number };
  direction?: 'ltr' | 'rtl';
  zones?: TGDZone[];
}
export interface TGDAnswers {
  zones?: Record<string, number>;
  choiceId?: string;
  reasonId?: string;
  firstTryWrong?: boolean;
}
export interface TGDRuleOutcome { passed: boolean; measured?: string }

const matches = (facts: TGDFacts, selector: TGDSelector): TGDComponent[] =>
  selectComponents(facts.design.components, selector);

const colorsOf = (design: TGDDesign, includeBackground = true): string[] => {
  const colors = design.components.flatMap(c =>
    c.strokeWidth && c.strokeWidth > 0 && c.stroke ? [c.fill, c.stroke] : [c.fill]);
  if (includeBackground) colors.push(design.background);
  return distinctColors(colors);
};

const hueDistance = (a: number, b: number): number => {
  const distance = Math.abs(a - b);
  return Math.min(distance, 360 - distance);
};

// L'arc couvrant le plus court est le cercle prive de son plus grand intervalle vide.
const palettePasses = (hues: number[], harmony: TGDHarmony, tolerance: number): boolean => {
  if (harmony === 'complementary') {
    return hues.some((a, i) => hues.some((b, j) =>
      j > i && Math.abs(hueDistance(a, b) - 180) <= tolerance));
  }
  if (harmony === 'analogous') {
    if (hues.length < 2) return false;
    const sorted = [...hues].sort((a, b) => a - b);
    const gaps = sorted.map((h, i) =>
      i === sorted.length - 1 ? sorted[0] + 360 - h : sorted[i + 1] - h);
    return 360 - Math.max(...gaps) <= 60 + tolerance;
  }
  if (harmony === 'triadic') {
    return hues.some((a, i) => hues.some((b, j) => j > i && hues.some((c, k) =>
      k > j && [hueDistance(a, b), hueDistance(a, c), hueDistance(b, c)]
        .every(d => Math.abs(d - 120) <= tolerance))));
  }
  return false;
};

const axisValue = (c: TGDComponent, axis: TGDAxis): number => {
  switch (axis) {
    case 'left': return c.x;
    case 'centerX': return c.x + c.width / 2;
    case 'right': return c.x + c.width;
    case 'top': return c.y;
    case 'centerY': return c.y + c.height / 2;
    case 'bottom': return c.y + c.height;
    default: {
      const exhaustive: never = axis;
      void exhaustive;
      throw new Error('axe inconnu');
    }
  }
};

const numericProps: TGDComparableProp[] = [
  'x', 'y', 'width', 'height', 'rotation', 'fontSize', 'sides', 'cornerRadius',
];
const colorProps: TGDComparableProp[] = ['fill', 'stroke', 'background'];
const propValue = (component: TGDComponent | TGDDesign, prop: TGDComparableProp): unknown =>
  (component as unknown as Record<string, unknown>)[prop];

const comparisonPasses = (current: TGDComponent | TGDDesign, initial: TGDComponent | TGDDesign,
  prop: TGDComparableProp, changed: boolean, tolerance: number): boolean => {
  const a = propValue(current, prop);
  const b = propValue(initial, prop);
  if (numericProps.includes(prop)) {
    const distance = Math.abs(
      (typeof a === 'number' ? a : 0) - (typeof b === 'number' ? b : 0)
    );
    return changed ? distance >= tolerance : distance <= tolerance;
  }
  if (colorProps.includes(prop)) {
    const left = typeof a === 'string' ? normalizeHex(a) : null;
    const right = typeof b === 'string' ? normalizeHex(b) : null;
    if (left === null || right === null) return changed ? left !== right : left === right;
    const distance = deltaE76(left, right) ?? 0;
    return changed ? distance >= tolerance : distance <= tolerance;
  }
  return changed ? a !== b : a === b;
};

// Preserved part de l'etat initial pour detecter une suppression.
const compareDesigns = (facts: TGDFacts, selector: TGDSelector, prop: TGDComparableProp,
  changed: boolean, tolerance: number): boolean => {
  if (!facts.initial) return false;
  if (prop === 'background') {
    return comparisonPasses(facts.design, facts.initial, prop, changed, tolerance);
  }
  const selected = changed ? matches(facts, selector)
    : selectComponents(facts.initial.components, selector);
  if (selected.length === 0) return false;
  return selected.every(component => {
    const peers = changed ? facts.initial!.components : facts.design.components;
    const peer = peers.find(c => c.id === component.id);
    if (!peer) return false;
    return changed ? comparisonPasses(component, peer, prop, true, tolerance)
      : comparisonPasses(peer, component, prop, false, tolerance);
  });
};

/**
 * Retourne le verdict d'une regle et sa mesure neutre.
 * Les donnees JSON malformees echouent sans interrompre les autres regles.
 */
export const evaluateRule = (rule: TGDRule, facts: TGDFacts): TGDRuleOutcome => {
  try {
    switch (rule.type) {
      case 'requiredElement': {
        const count = rule.params.select.by === 'background'
          ? 1 : matches(facts, rule.params.select).length;
        const passed = rule.params.select.by === 'background'
          || count >= (rule.params.minCount ?? 1);
        return { passed, measured: String(count) };
      }
      case 'elementCountMax': {
        const count = rule.params.componentType
          ? facts.design.components.filter(c => c.type === rule.params.componentType).length
          : facts.design.components.length;
        return { passed: count <= rule.params.value, measured: String(count) };
      }
      case 'maxColors':
      case 'minColors': {
        const count = colorsOf(facts.design, rule.params.includeBackground !== false).length;
        const passed = rule.type === 'maxColors'
          ? count <= rule.params.value : count >= rule.params.value;
        return { passed, measured: String(count) };
      }
      case 'contrastMin': {
        const a = matches(facts, rule.params.a);
        const b = rule.params.b ?? { by: 'background' };
        const colorB = b.by === 'background' ? facts.design.background : matches(facts, b)[0]?.fill;
        if (a.length === 0 || !colorB) return { passed: false, measured: 'absent' };
        const ratios = a.map(c => contrastRatio(c.fill, colorB));
        if (ratios.some(r => r === null)) return { passed: false, measured: 'absent' };
        const minimum = Math.min(...(ratios as number[]));
        return { passed: minimum >= rule.params.value, measured: minimum.toFixed(2) };
      }
      case 'usesPalette': {
        const hues = colorsOf(facts.design).filter(c => !isNeutral(c)).map(c => hexToHsl(c)!.h);
        const passed = palettePasses(hues, rule.params.harmony, rule.params.toleranceDeg ?? 20);
        return { passed, measured: String(hues.length) };
      }
      case 'sizeRatioMin': {
        const a = matches(facts, rule.params.a);
        const b = matches(facts, rule.params.b);
        if (!a.length || !b.length) return { passed: false, measured: 'absent' };
        const divisor = Math.max(...b.map(sizeOf));
        if (divisor === 0) return { passed: false, measured: 'absent' };
        const ratio = Math.max(...a.map(sizeOf)) / divisor;
        return { passed: ratio >= rule.params.value, measured: ratio.toFixed(2) };
      }
      case 'hierarchyOrder': {
        if (!Array.isArray(rule.params.order) || rule.params.order.length === 0) {
          return { passed: false, measured: 'absent' };
        }
        const scores = rule.params.order.map(selector => {
          const found = matches(facts, selector);
          return found.length ? Math.max(...found.map(c =>
            salienceOf(c, facts.design.background, facts.artboard, facts.direction))) : null;
        });
        if (scores.some(score => score === null)) return { passed: false, measured: 'absent' };
        const values = scores as number[];
        const passed = values.every((score, i) =>
          i === 0 || values[i - 1] >= score * (rule.params.minRatio ?? 1.15));
        return { passed, measured: values.map(score => score.toFixed(1)).join(' > ') };
      }
      case 'alignedOn': {
        const components = Array.from(new Map(rule.params.select.flatMap(selector => matches(facts, selector))
          .map(c => [c.id, c] as const)).values());
        if (components.length < 2) return { passed: false, measured: 'absent' };
        const values = components.map(c => axisValue(c, rule.params.axis));
        const spread = Math.max(...values) - Math.min(...values);
        return {
          passed: spread <= (rule.params.tolerance ?? 4),
          measured: String(Math.round(spread * 10) / 10),
        };
      }
      case 'timeMax': {
        const seconds = Math.trunc(facts.elapsedMs / 1000);
        return { passed: facts.elapsedMs <= rule.params.seconds * 1000, measured: String(seconds) };
      }
      case 'zoneFound': {
        const zone = facts.zones?.find(z => z.id === rule.params.zoneId);
        return { passed: !!zone && facts.answers.zones?.[zone.id] === zone.correct };
      }
      case 'choiceEquals': return { passed: facts.answers.choiceId === rule.params.choiceId };
      case 'reasonEquals': return { passed: facts.answers.reasonId === rule.params.reasonId };
      case 'changed': return { passed: compareDesigns(facts, rule.params.select, rule.params.prop, true,
        rule.params.minDelta ?? (numericProps.includes(rule.params.prop) ? 1 : 10)) };
      case 'preserved': return { passed: compareDesigns(facts, rule.params.select, rule.params.prop, false,
        rule.params.tolerance ?? (numericProps.includes(rule.params.prop) ? 0.5 : 3)) };
      default: {
        const exhaustive: never = rule;
        void exhaustive;
        return { passed: false };
      }
    }
  } catch {
    return { passed: false, measured: 'erreur' };
  }
};
